import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IonIcon } from '@ionic/react';
import { IonicButton, IonicInput, AddBankModal } from 'ionic-ui';
import { checkmarkCircleOutline, addCircleOutline } from 'ionicons/icons';
import clsx from 'clsx';
import DialogTitle from '../../../../DialogTitle';
import { RootState } from '../../../../../../store/store';
import { useDialog } from '../../../../../../context/Dialog';
import useIonicToast from '../../../../../../hooks/useIonicToast';
import { formatPrice } from '../../../../../../utils/formatPrice';
import { baseURL } from '../../../../../../data/config';
import {
  externalPayout,
  getPayoutMethods,
  walletToWalletTransfer,
  PayoutMethod,
} from '../../../../../../services/walletSetup/walletOps/walletOps';
import PinField from './PinField';
import SetWalletPinDialog from './SetWalletPinDialog';

type Props = {
  fromAccountNumber: string;
  availableBalance: number;
};

type Destination = 'wallet' | 'bank';
type Step = 'details' | 'pin' | 'done';

const tabClass = (active: boolean) =>
  clsx(
    'flex-1 py-2 rounded-lg text-sm font-medium transition',
    active
      ? 'bg-primary text-white'
      : 'bg-white border border-gray-300 text-text-dark-high hover:bg-gray-50'
  );

/**
 * Single "Send money" flow covering both destinations a wallet can pay
 * out to — another Swiftmedix wallet (instant, free) or an external bank
 * account (NIBSS interbank rail, ~1hr). Splitting these into separate
 * "Transfer" / "Withdraw" buttons read as two different features to users
 * when they're really just two destination types for the same action.
 */
const SendMoneyDialog: React.FC<Props> = ({
  fromAccountNumber,
  availableBalance,
}) => {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const { openDialog, closeDialog } = useDialog();
  const { presentIonToast } = useIonicToast();
  const queryClient = useQueryClient();

  const { data: methods, isLoading: methodsLoading } = useQuery({
    queryKey: ['payout-methods'],
    queryFn: () => getPayoutMethods({ authToken: authToken! }),
    enabled: Boolean(authToken),
  });
  const bankMethods = (methods || []).filter(
    (m) => m.type !== 'embedly_wallet'
  );

  const [destination, setDestination] = useState<Destination>('wallet');
  const [step, setStep] = useState<Step>('details');
  const [toAccount, setToAccount] = useState('');
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [addBankOpen, setAddBankOpen] = useState(false);

  const parsedAmount = Number(amount);
  const selectedMethod: PayoutMethod | undefined = bankMethods.find(
    (m) => String(m.id) === methodId
  );

  const destinationLabel =
    destination === 'wallet'
      ? `wallet ${toAccount}`
      : `${selectedMethod?.bank_name || 'bank'} ****${selectedMethod?.account_number?.slice(-4) || ''}`;

  const mutation = useMutation({
    mutationFn: () =>
      destination === 'wallet'
        ? walletToWalletTransfer({
            authToken: authToken!,
            payload: {
              pin,
              from_account_number: fromAccountNumber,
              to_account_number: toAccount.trim(),
              amount: parsedAmount,
              remarks: remarks.trim() || undefined,
            },
          })
        : externalPayout({
            authToken: authToken!,
            payload: {
              pin,
              from_account_number: fromAccountNumber,
              to_account_number: selectedMethod!.account_number!,
              to_bank_code: selectedMethod!.bank_code!,
              to_bank_name: selectedMethod!.bank_name || '',
              to_account_name:
                selectedMethod!.verified_account_name ||
                selectedMethod!.account_name ||
                '',
              amount: parsedAmount,
              remarks: remarks.trim() || undefined,
            },
          }),
    onSuccess: (res: any) => {
      setReference(res?.reference || null);
      setStep('done');
      presentIonToast(
        destination === 'wallet'
          ? 'Transfer successful.'
          : 'Withdrawal initiated.'
      );
      queryClient.invalidateQueries({ queryKey: ['wallet-status'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
    onError: (err: any) => {
      const message = err?.message || 'Something went wrong. Please try again.';
      if (err?.code === 404 && /pin not set/i.test(message)) {
        openDialog(<SetWalletPinDialog />, {
          allowBackgroundDismiss: false,
        });
        setError('Set your wallet PIN, then retry.');
        return;
      }
      setError(message);
      setPin('');
    },
  });

  const validateDetails = () => {
    setError(null);
    if (destination === 'wallet') {
      if (!/^\d{10}$/.test(toAccount.trim())) {
        setError('Enter a valid 10-digit wallet account number.');
        return;
      }
      if (toAccount.trim() === fromAccountNumber) {
        setError('You cannot transfer to your own wallet.');
        return;
      }
    } else {
      if (!selectedMethod) {
        setError('Select a bank account to withdraw to.');
        return;
      }
    }
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    if (parsedAmount > availableBalance) {
      setError('Amount exceeds your available balance.');
      return;
    }
    setStep('pin');
  };

  const submitPin = () => {
    setError(null);
    if (pin.length !== 6) {
      setError('Enter your 6-digit wallet PIN.');
      return;
    }
    mutation.mutate();
  };

  if (step === 'done') {
    return (
      <div className="space-y-4 sm:max-w-[420px] dialog-content text-center">
        <IonIcon
          icon={checkmarkCircleOutline}
          className="size-14 text-green-500 mx-auto"
        />
        <h5 className="font-semibold high-emphasis-text">
          {destination === 'wallet' ? 'Transfer sent' : 'Withdrawal initiated'}
        </h5>
        <p className="text-sm medium-emphasis-text">
          {formatPrice(parsedAmount)}{' '}
          {destination === 'wallet' ? 'was sent to' : 'is on its way to'}{' '}
          {destinationLabel}.
        </p>
        {destination === 'bank' && (
          <p className="text-xs medium-emphasis-text">
            Bank transfers can take up to an hour to arrive.
          </p>
        )}
        {reference && (
          <p className="text-xs font-mono medium-emphasis-text break-all">
            Ref: {reference}
          </p>
        )}
        <IonicButton
          className="w-full"
          type="button"
          onClick={() => closeDialog()}
        >
          Done
        </IonicButton>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:max-w-[420px] dialog-content">
      <DialogTitle>Send money</DialogTitle>

      {step === 'details' && (
        <>
          <div className="flex gap-2">
            <button
              type="button"
              className={tabClass(destination === 'wallet')}
              onClick={() => {
                setDestination('wallet');
                setError(null);
              }}
            >
              To a wallet
            </button>
            <button
              type="button"
              className={tabClass(destination === 'bank')}
              onClick={() => {
                setDestination('bank');
                setError(null);
              }}
            >
              To my bank
            </button>
          </div>

          <p className="text-sm medium-emphasis-text">
            {destination === 'wallet' ? (
              <>Send to another Swiftmedix wallet — instant and free.</>
            ) : (
              <>Send to your bank account — usually within an hour.</>
            )}{' '}
            Available balance: <strong>{formatPrice(availableBalance)}</strong>
          </p>

          {destination === 'wallet' ? (
            <div className="space-y-3">
              <IonicInput
                label="Recipient wallet account number"
                type="text"
                placeholder="0123456789"
                value={toAccount}
                onChange={(e: any) =>
                  setToAccount(
                    String(e.target?.value ?? '')
                      .replace(/\D/g, '')
                      .slice(0, 10)
                  )
                }
              />
            </div>
          ) : !methodsLoading && bankMethods.length === 0 ? (
            <div className="p-3 rounded-lg bg-black/5 dark:bg-white/5 text-sm medium-emphasis-text space-y-2">
              <p>You haven't added a bank account yet.</p>
              <button
                type="button"
                onClick={() => setAddBankOpen(true)}
                className="inline-flex items-center gap-1.5 font-medium text-primary"
              >
                <IonIcon icon={addCircleOutline} className="size-4" />
                Add a bank account
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <IonicInput
                label="Withdraw to"
                type="select"
                placeholder="Select bank account"
                value={methodId}
                options={bankMethods.map((m) => ({
                  label: `${m.bank_name || 'Bank'} ****${m.account_number?.slice(-4) || ''}`,
                  value: String(m.id),
                }))}
                onChange={(e: any) =>
                  setMethodId(String(e.target?.value ?? ''))
                }
              />
              <button
                type="button"
                onClick={() => setAddBankOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary"
              >
                <IonIcon icon={addCircleOutline} className="size-3.5" />
                Add another bank account
              </button>
            </div>
          )}

          <div className="space-y-3">
            <IonicInput
              label="Amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e: any) => setAmount(String(e.target?.value ?? ''))}
            />
            <IonicInput
              label="Remarks (optional)"
              type="text"
              placeholder="What's this for?"
              value={remarks}
              onChange={(e: any) => setRemarks(String(e.target?.value ?? ''))}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2 justify-end">
            <IonicButton
              variant="outline"
              type="button"
              onClick={() => closeDialog()}
            >
              Cancel
            </IonicButton>
            <IonicButton
              type="button"
              onClick={validateDetails}
              disabled={destination === 'bank' && bankMethods.length === 0}
            >
              Continue
            </IonicButton>
          </div>
        </>
      )}

      {step === 'pin' && (
        <>
          <div className="p-3 rounded-lg bg-black/5 dark:bg-white/5 text-sm medium-emphasis-text space-y-1">
            <div className="flex justify-between">
              <span>To</span>
              <span className="font-mono">{destinationLabel}</span>
            </div>
            <div className="flex justify-between">
              <span>Amount</span>
              <span className="font-semibold">{formatPrice(parsedAmount)}</span>
            </div>
          </div>
          <PinField
            label="Enter your wallet PIN to authorize"
            value={pin}
            onChange={setPin}
            autoFocus
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2 justify-end">
            <IonicButton
              variant="outline"
              type="button"
              onClick={() => {
                setError(null);
                setStep('details');
              }}
            >
              Back
            </IonicButton>
            <IonicButton
              type="button"
              onClick={submitPin}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? 'Sending…'
                : destination === 'wallet'
                  ? 'Confirm transfer'
                  : 'Confirm withdrawal'}
            </IonicButton>
          </div>
        </>
      )}

      {addBankOpen && (
        <AddBankModal
          baseURL={baseURL}
          authHeaders={{
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          }}
          onClose={() => setAddBankOpen(false)}
          onAdded={(created) => {
            setAddBankOpen(false);
            queryClient.invalidateQueries({ queryKey: ['payout-methods'] });
            if (created?.id) setMethodId(String(created.id));
          }}
        />
      )}
    </div>
  );
};

export default SendMoneyDialog;
